import { axios } from '@/plugins/axios';
import { formatDateWithOriginToIso } from '@/services/dateFormatter';
import { FolderUpdate, InjectionType, PrescriptionType, Ps, VaccinationFolder } from '@/store/folder/types';
import { InformationsCiblageType } from '@/store/infosCiblage/types';
import _ from 'lodash';
import { InfosDelegation } from '@/components/MesParametres/ParametresVaccination/ParametresDelegation/types';

export function updatePrescription(
	folder: {
		dossier: VaccinationFolder,
		prescription: PrescriptionType,
		prescriptionId: number
	},
	informationsCiblage: InformationsCiblageType | null,
	psDelegation: InfosDelegation | undefined) {
	const numeroBon = _.get(folder, 'dossier.numeroBon');
	const prescriptionFormat = _.pick(_.get(folder, 'prescription'), [
		'datePrescription', 'prescriptEnregistreur'
	]);
	const prescripteurFormat = _.omit(_.get(folder, 'prescription.prescripteur'), [
		'psId', 'organismeRattachement', 'informationContact'
	]);
	return axios.post(
		'/vaccination-covid-api/folders/prescription/update',
		{
			numeroBon,
			prescription: {
				prescriptionId: folder.prescriptionId,
				prescripteur: prescripteurFormat,
				...prescriptionFormat,
				psDelegation
			},
			ciblageInfo: informationsCiblage
		});
}

export function postPrescription(
	folder: {
		dossier: VaccinationFolder,
		prescription: PrescriptionType
	},
	informationsCiblage: InformationsCiblageType | null,
	psDelegation: InfosDelegation | undefined) {
	const dossierFormat = _.omit(_.get(folder, 'dossier'), ['prescription']);
	const prescriptionFormat = {
		..._.omit(_.get(folder, 'prescription'), [
			'prescriptionId', 'horodateEnregistrement', 'majDossierVacc',
			'prescripteur.psId', 'enregistreur.psId']),
		psDelegation
	};

	return axios.post(
		'/vaccination-covid-api/folders/prescription',
		{ dossier: dossierFormat, prescription: prescriptionFormat, ciblageInfo: informationsCiblage });
}

export function postVaccination(
	injection: InjectionType,
	informationsCiblage: InformationsCiblageType | undefined | null,
	isEditionMode: boolean,
	psDelegation: InfosDelegation | undefined,
	numeroBon?: string,
	codeCiblage?: string,
	motifInjectionSuppId?: number
) {
	if (isEditionMode) {
		return updateVaccination(numeroBon!, injection, informationsCiblage, psDelegation, motifInjectionSuppId);
	} else {
		return saveVaccination(injection, informationsCiblage, psDelegation, codeCiblage, motifInjectionSuppId);
	}
}

function saveVaccination(
	injection: InjectionType,
	informationsCiblage: InformationsCiblageType | undefined | null,
	psDelegation: InfosDelegation | undefined,
	codeCiblage?: string,
	motifInjectionSuppId?: number) {
	return axios.post(
		'/vaccination-covid-api/injection',
		{
			ciblageInfo: informationsCiblage,
			codeCiblage,
			injection: {
				..._.omit(injection, [
					'dateEnregistrement',
					'dateProchaineInjection',
					'dateRealisation',
					'injectionId',
					'vaccin',
					'categorieLieu'
				]),
				vaccin: _.pick(injection.vaccin, ['vaccinId']),
				categorieLieu: _.pick(injection.categorieLieu, ['categorieLieuId']),
				dateRealisation: formatDateWithOriginToIso(`${injection.dateRealisation} ${injection.horaireVaccination}`),
				psDelegation,
				motifInjectionSupp: motifInjectionSuppId === undefined ? null : { motifId: motifInjectionSuppId }
			}
		}
	);
}

function updateVaccination(
	numeroBon: string,
	injection: InjectionType,
	informationsCiblage: InformationsCiblageType | undefined | null,
	psDelegation: InfosDelegation | undefined,
	motifInjectionSuppId?: number) {
	return axios.post(
		'/vaccination-covid-api/injection/update',
		{
			numeroBon,
			ciblageInfo: informationsCiblage,
			injection: {
				..._.omit(injection, [
					'dateEnregistrement',
					'dateProchaineInjection',
					'dateRealisation',
					'vaccin',
					'categorieLieu',
					'topEnregistrementMaj',
					'horodateEnregistrementMaj',
					'vaccinateur'
				]),
				vaccinId: _.get(injection.vaccin, ['vaccinId']),
				categorieLieuId: _.get(injection.categorieLieu, ['categorieLieuId']),
				dateRealisation: formatDateWithOriginToIso(`${injection.dateRealisation} ${injection.horaireVaccination}`),
				psDelegation,
				motifInjectionSuppId
			}
		}
	);
}

export function deletePrescription(
	numeroBon: string | undefined,
	prescriptionId: number | undefined,
	motifSuppresionId: number | undefined,
	psDelegation: InfosDelegation | undefined
) {
	return axios.delete(
		'/vaccination-covid-api/folders/prescription', {
			data: {
				numeroBon,
				prescriptionId,
				motifSuppresionId,
				psDelegation
			}
		});
}

export function deleteInjection(
	numeroBon: string | undefined,
	prescriptionId: number | undefined,
	injectionId: number | undefined,
	motifSuppresionId: number | undefined,
	psDelegation: InfosDelegation | undefined
) {
	return axios.delete(
		'/vaccination-covid-api/injection', {
			data: {
				numeroBon,
				prescriptionId,
				injectionId,
				motifSuppresionId,
				psDelegation
			}
		});
}

export function updateFolder(
	numeroBon: string | undefined,
	dossierUpdate: FolderUpdate,
	psDelegation: InfosDelegation | undefined,
	psModificateur: Ps,
	prescriptionId: number | undefined
) {
	return axios.post(
		'/vaccination-covid-api/folders/update', {
			numeroBon,
			dossierUpdate,
			psDelegation,
			psModificateur,
			prescriptionId
		}
	);
}

export function updateFolderAuto(
	nir: string,
	dateNaissance: string,
	rang: string,
	prescriptionId: number | undefined,
	psDelegation: InfosDelegation | undefined,
	psModificateur: Ps,
	updateRattachement: boolean
) {
	return axios.post(
		'/vaccination-covid-api/folders/updateAuto', {
			nir,
			dateNaissance,
			rang,
			prescriptionId,
			psDelegation,
			psModificateur
		}
	);
}
